import dayjs from "dayjs";
import { SummaryReportQuery } from "./summaryReportService";

export interface FilterOption {
  value: string;
  label: string;
  group?: string;
  stageIds?: string[];
}

export interface FilterOptionGroup {
  label: string;
  options: FilterOption[];
}

export interface SummaryFilterState {
  lifecycles: FilterOption[];
  stages: FilterOption[];
  status: FilterOption | null;
  reasons: FilterOption[];
  teams: FilterOption[];
  users: FilterOption[];
  source: FilterOption | null;
  priorities: FilterOption[];
  products: FilterOption[];
  currencies: FilterOption[];
  startDate: string | null;
  endDate: string | null;
}

export const emptySummaryFilters: SummaryFilterState = {
  lifecycles: [],
  stages: [],
  status: null,
  reasons: [],
  teams: [],
  users: [],
  source: null,
  priorities: [],
  products: [],
  currencies: [],
  startDate: null,
  endDate: null,
};

export const PRIORITY_OPTIONS: FilterOption[] = ["High", "Medium", "Low"].map((value) => ({ value, label: value }));

export const BASE_STATUS_OPTIONS: FilterOption[] = [
  { value: "open", label: "Open" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
];

const DISPLAY_DATE_FORMAT = "DD MMM YYYY";

export const formatSummaryDateRange = (startDate: string | null, endDate: string | null) =>
  startDate && endDate
    ? `${dayjs(startDate).format(DISPLAY_DATE_FORMAT)} - ${dayjs(endDate).format(DISPLAY_DATE_FORMAT)}`
    : "";

const values = (options: FilterOption[]) => options.map((option) => option.value);

export const resolveStageIds = (lifecycles: FilterOption[], stages: FilterOption[]) => {
  const selectedGroups = new Set(lifecycles.map((lifecycle) => lifecycle.value));
  const ids = stages.filter((stage) => !stage.group || !selectedGroups.size || selectedGroups.has(stage.group)).map((stage) => stage.value);
  lifecycles.forEach((lifecycle) => {
    if (!stages.some((stage) => stage.group === lifecycle.value)) ids.push(...(lifecycle.stageIds ?? []));
  });
  return Array.from(new Set(ids));
};

export const withoutLifecycleStages = (stages: FilterOption[], lifecycles: FilterOption[]) => {
  if (!lifecycles.length) return [];
  const allowed = new Set(lifecycles.map((lifecycle) => lifecycle.value));
  return stages.filter((stage) => !stage.group || allowed.has(stage.group));
};

export const toSummaryQuery = (filters: SummaryFilterState, salesUserId: number | null): SummaryReportQuery => ({
  stageIds: resolveStageIds(filters.lifecycles, filters.stages),
  statuses: filters.status ? [filters.status.value] : [],
  reasonIds: filters.status ? values(filters.reasons) : [],
  teamIds: values(filters.teams),
  userIds: salesUserId ? [] : values(filters.users),
  sourceIds: filters.source ? [filters.source.value] : [],
  priorities: values(filters.priorities),
  productIds: values(filters.products),
  currencyCodes: values(filters.currencies),
  startDate: filters.startDate,
  endDate: filters.endDate,
  salesUserId,
});

export interface SummaryFilterChip {
  key: string;
  label: string;
  next: SummaryFilterState;
}

const listLabel = (title: string, options: FilterOption[]) =>
  options.length === 1 ? `${title}: ${options[0].label}` : `${title}: ${options.length} selected`;

export const getSummaryFilterChips = (filters: SummaryFilterState): SummaryFilterChip[] => {
  const chips: SummaryFilterChip[] = [];
  const listChip = (key: keyof SummaryFilterState, title: string) => {
    const selected = filters[key] as FilterOption[];
    if (selected.length) chips.push({ key, label: listLabel(title, selected), next: { ...filters, [key]: [] } });
  };
  if (filters.lifecycles.length) {
    chips.push({
      key: "lifecycles",
      label: listLabel("Lifecycle", filters.lifecycles),
      next: { ...filters, lifecycles: [], stages: [] },
    });
  }
  listChip("stages", "Stage");
  if (filters.status) {
    chips.push({
      key: "status",
      label: `Status: ${filters.status.label}`,
      next: { ...filters, status: null, reasons: [] },
    });
  }
  listChip("reasons", "Reason");
  listChip("teams", "Team");
  listChip("users", "Assigned User");
  if (filters.source) {
    chips.push({ key: "source", label: `Source: ${filters.source.label}`, next: { ...filters, source: null } });
  }
  listChip("priorities", "Priority");
  listChip("products", "Products & Services");
  listChip("currencies", "Currency");
  if (filters.startDate && filters.endDate) {
    chips.push({
      key: "date",
      label: formatSummaryDateRange(filters.startDate, filters.endDate),
      next: { ...filters, startDate: null, endDate: null },
    });
  }
  return chips;
};

export const countSummaryFilters = (filters: SummaryFilterState) => getSummaryFilterChips(filters).length;
